/**
 * PendingAnnouncements — WhatsApp game updates waiting for the keeper.
 * 2026-10-17 (Press Box).
 *
 * With "check before sending" on (the default), every automatic update — a
 * goal the voice AI heard, a penalty, a period, the final, the official
 * result — waits here for Send or Skip instead of going straight to every
 * parent. Live updates expire after 20 minutes; results after a day.
 *
 * Renders nothing when there is nothing to decide (or the viewer is not the
 * coach / a stat keeper). Refreshes on the team socket's WA_PENDING.
 */
import { useCallback, useEffect, useState } from 'react';
import { Send, X, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import { chatSocket } from '../services/socket.js';

const KIND_LABEL = { goal: 'Goal', play: 'Play', period: 'Period', final: 'Final', official: 'Official result' };

function ago(iso) {
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)} h ago`;
}

export default function PendingAnnouncements({ teamId, gameId = null, title = 'Waiting for you to send to parents' }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [, tick] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/teams/${teamId}/whatsapp/pending`, { params: gameId ? { gameId } : {} });
      setItems(data.pending || []);
    } catch { setItems([]); }
  }, [teamId, gameId]);

  useEffect(() => {
    load();
    chatSocket.joinTeam(teamId);
    // Any of the team's held updates may belong here (a mini-game is a
    // different game id than the root the live page sits on): just reload.
    const off = chatSocket.on('WA_PENDING', () => load());
    const poll = setInterval(() => { load(); tick(x => x + 1); }, 30000);
    return () => { off?.(); clearInterval(poll); };
  }, [teamId, gameId, load]);

  const decide = async (item, action) => {
    setBusy(item.id + action);
    try {
      const { data } = await api.post(`/teams/${teamId}/whatsapp/pending/${item.id}/${action}`);
      if (action === 'send') toast.success(data.sentTo ? `Sent to ${data.sentTo} parent${data.sentTo === 1 ? '' : 's'}` : 'Sent (no parents subscribed yet)');
      setItems(xs => xs.filter(x => x.id !== item.id));
    } catch (e) {
      toast.error(e.response?.data?.error || 'Could not do that');
      load();
    } finally { setBusy(null); }
  };
  const sendAll = async () => {
    if (!gameId) return;
    setBusy('all');
    try {
      const { data } = await api.post(`/teams/${teamId}/whatsapp/pending/send-all`, { gameId });
      toast.success(`Sent ${data.sent} update${data.sent === 1 ? '' : 's'}`);
      load();
    } catch { toast.error('Could not send'); } finally { setBusy(null); }
  };

  if (!items.length) return null;
  return (
    <div className="rounded-xl mb-3" data-pending-announcements
      style={{ border: '1px solid #25D366', background: 'rgba(37,211,102,0.07)', overflow: 'hidden' }}>
      <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid rgba(37,211,102,0.25)' }}>
        <span className="flex items-center gap-2 text-xs font-bold" style={{ color: 'var(--text)' }}>
          <MessageCircle size={14} style={{ color: '#25D366' }} /> {title} ({items.length})
        </span>
        {gameId && items.length > 1 && (
          <button type="button" onClick={sendAll} disabled={!!busy} className="text-xs font-bold px-2 py-1 rounded-lg"
            style={{ background: '#25D366', color: '#000', border: 'none', cursor: busy ? 'wait' : 'pointer' }}>
            Send all
          </button>
        )}
      </div>
      {items.map(it => (
        <div key={it.id} className="flex items-start gap-2 px-3 py-2" style={{ borderTop: '1px solid var(--border)' }}>
          <div className="flex-1 min-w-0">
            <div className="text-xs" style={{ color: 'var(--text-3)' }}>{KIND_LABEL[it.kind] || it.kind} · {ago(it.createdAt)}</div>
            <div className="text-sm" style={{ color: 'var(--text)', overflowWrap: 'anywhere' }}>{it.body}</div>
          </div>
          <button type="button" onClick={() => decide(it, 'send')} disabled={!!busy} title="Send to every subscribed parent"
            className="flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg"
            style={{ background: '#25D366', color: '#000', border: 'none', cursor: busy ? 'wait' : 'pointer' }}>
            <Send size={12} /> Send
          </button>
          <button type="button" onClick={() => decide(it, 'skip')} disabled={!!busy} title="Don't send this one"
            className="flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg"
            style={{ background: 'none', color: 'var(--text-2)', border: '1px solid var(--border)', cursor: busy ? 'wait' : 'pointer' }}>
            <X size={12} /> Skip
          </button>
        </div>
      ))}
    </div>
  );
}
